import useFetch from "@/hooks/use-fetch";
import { extractMessageFromError } from "@/shared/notification";
import { BaseQuery, QueryResponse } from "@/shared/types/base";
import { Autocomplete, AutocompleteCloseReason, CircularProgress, SxProps, TextField, Theme } from "@mui/material";
import { forwardRef, ReactNode, Ref, useEffect, useState } from "react";

export type ModelId = string;
export type Model = { id: ModelId } & Record<string, any>
export type GetFunction<T extends Model> = (id: ModelId) => Promise<T>
export type GetAllFunction<T extends Model, P extends BaseQuery> = (filter: P, count: number) => Promise<T[] | QueryResponse<T>>

export interface ModelComboBoxProps<T extends Model, P extends BaseQuery> {
    value?: ModelId | "",
    setValue?: (value: ModelId | "") => void,
    onObjectSelected?: (object: T | null) => void,

    get: (id: ModelId) => Promise<T>,
    getAll: (filter: P, count: number) => Promise<T[] | QueryResponse<T>>,
    labelName?: keyof T,
    renderOption?: (option: T) => ReactNode,
    optionCount?: number,

    label?: ReactNode,
    disabled?: boolean,
    size?: "medium" | "small",
    sx?: SxProps<Theme>,
    error?: boolean,
    helperText?: string,
    onBlur?: React.FocusEventHandler<HTMLInputElement>,
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>,
    onClose?: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void
}

const ModelComboBox = forwardRef(<T extends Model, P extends BaseQuery>({
    get,
    getAll,

    value,
    setValue = () => { },
    onObjectSelected,
    labelName = "nombre",
    renderOption = (o) => o[labelName],
    optionCount = 10,

    sx,
    disabled,
    size,
    onClose,
    ...props
}: ModelComboBoxProps<T, P>, ref: any) => {
    const [_initialLoading, _setInitialLoading] = useState(true);
    const [_value, _setValue] = useState<T | null>(null);

    const { fetch: _initialValue, loading: _valueLoading, error: _valueError } = useFetch(async (value: ModelId) => {
        if (value) return await get(value);
        return null;
    }, {
        onSuccess: v => {
            _setInitialLoading(false);
            _setValue(v)
        },
        onError: () => {
            _setInitialLoading(false);
        }
    });

    const { fetch: _fetchOptions, value: _options, error: _error, loading: _loading } = useFetch(async (inputValue: string) => {
        const result = await getAll({ [labelName]: inputValue, pageSize: optionCount } as any, optionCount);
        if ("result" in result) return result.result;
        else return result;
    }, {
        debounceMs: 500,
        initialValue: [],
        placeholderValue: [],
    })

    useEffect(() => {
        _initialValue(value || "");
        if (!value) _fetchOptions("");
    }, [value]);

    return <Autocomplete
        options={_options!}
        isOptionEqualToValue={(o, v) => o.id === v.id}

        noOptionsText="Ninguna Opción"
        renderOption={({ key, ...props }, option) => {
            return <li key={key} {...props}>{renderOption(option)}</li>
        }}

        renderInput={(params) => <TextField
            {...props}
            {...params}
            inputRef={ref}
            slotProps={{
                input: {
                    ...params.InputProps,
                    endAdornment: <>
                        {(_initialLoading || _valueLoading || _loading) && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                    </>
                },
            }}
        />}
        onInputChange={(_, v, reason) => {
            // Solo re-consultar opciones cuando el usuario está tipeando. Al elegir una opción
            // (reason "reset") o limpiar (reason "clear") no hace falta: el objeto ya se conoce
            // (seleccionado de la lista) o el efecto de "value" vacío ya recarga las opciones.
            if (reason === "input") _fetchOptions(v);
        }}

        value={_value}
        onChange={(_, v) => {
            _setValue(v);
            if (v) setValue(v.id);
            else setValue("");
            onObjectSelected && onObjectSelected(v);
        }}
        onClose={onClose}

        size={size}
        autoSelect
        loading={_initialLoading || _valueLoading || _loading || Boolean(_error)}
        loadingText={_valueError ? extractMessageFromError(_valueError) : _error ? extractMessageFromError(_error) : "Cargando..."}
        disabled={disabled || _initialLoading || Boolean(_error)}
        getOptionLabel={o => labelName in o ? String(o[labelName]) : o.id}
        sx={sx}
    />
})

export default ModelComboBox as <T extends Model, P extends BaseQuery>(props: ModelComboBoxProps<T, P> & { ref?: Ref<HTMLInputElement> }) => ReactNode;